import React, { useCallback, useEffect } from 'react';
import { makeStyles } from '@material-ui/styles';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Grid,
  Typography
} from '@material-ui/core';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import {
  APP_PATH_EDITOR_JOB,
  APP_PATH_PACKAGE_PLAN,
  BASE_END_POINT_COMPANY
} from '../../config';
import { getLocalStorageAuthentication } from '../../lib/auth';
import RocketLaunchIcon from '@material-ui/icons/FlightTakeoff';
import WorkIcon from '@material-ui/icons/Work';
import JobListByUser from './components/JobListByUser';
import PaymentRequiredDialog from '../../components/PaymentRequiredDialog/PaymentRequiredDialog';
import { DeleteJobConfirmationDialog } from '../../components';
import usePageTitle from '../../hooks/usePageTitle';
import {
  clearUpdatingJobId,
  clearUserJobs,
  closeDeleteConfirmationDialog,
  closePaymentDialog,
  deleteConfirmationJobSelector,
  deleteDialogOpenSelector,
  deleteUserJob,
  draftJobUpdated,
  fetchUserJobs,
  openDeleteConfirmationDialog,
  paymentDialogOpenSelector,
  paymentRequiredJobSelector,
  refreshUserJobsSelector,
  resetRefreshUserJobs,
  setUpdatingJobId,
  updateUserJob,
  updatingJobIdSelector,
  userEnrichedJobListSelector,
  userJobListFetchedSelector,
  userJobListLoadingSelector,
  userJobListSelector
} from '../../redux/features/job/jobsSlice';
import {
  applicationsFetchedSelector,
  fetchCompanyApplications
} from '../../redux/features/applications/applicationsSlice';
import { useCurrentUserInfo } from '../../hooks/useCurrentUserInfo';
import { STATUS_ACTIVE, STATUS_INACTIVE } from '../../common/adapters';
import { messageUpdated } from '../../redux/features/app/appSlice';
import { Severity } from '../../components/Message/Message';
import { clearDraftJobSession } from '../../lib/sessionStorage';
import { draftCompanyUpdated } from '../../redux/features/company/companiesSlice';
import { createFileForUserSucceeded } from '../../redux/features/file/filesSlice';
import {
  bundleHistoryLastFetchedSelector,
  bundleHistoryLoadingSelector,
  bundleLastFetchedSelector,
  bundleLoadingSelector,
  fetchBundleHistory,
  fetchBundleStatus
} from '../../redux/features/bundle/bundleSlice';
import {
  trackBuyPlanClicked,
  trackDashboardView,
  trackDeactivateInsteadOfDelete,
  trackDeleteDialogKept,
  trackDeleteDialogShown,
  trackJobActivated,
  trackJobActivationPaymentRequired,
  trackJobDeactivated,
  trackJobDeleted,
  trackJobEditClicked,
  trackPaymentDialogDismissed,
  trackPostFirstJobClicked
} from '../../helpers';

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(4)
  },
  loadingContainer: {
    backgroundColor: theme.palette.background.default,
    minHeight: '70vh',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    padding: theme.spacing(3)
  },
  loadingIcon: {
    color: theme.palette.primary.main,
    marginBottom: theme.spacing(3)
  },
  emptyStateContainer: {
    backgroundColor: theme.palette.background.default,
    minHeight: '70vh',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    padding: theme.spacing(3)
  },
  emptyStateCard: {
    maxWidth: 700,
    width: '100%',
    textAlign: 'center',
    padding: theme.spacing(6),
    background: `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${theme.palette.background.default} 100%)`,
    boxShadow: '0 10px 40px rgba(0,0,0,0.1)',
    borderRadius: theme.spacing(2)
  },
  rocketIcon: {
    fontSize: 120,
    color: theme.palette.primary.main,
    marginBottom: theme.spacing(3),
    animation: '$float 3s ease-in-out infinite'
  },
  emptyStateTitle: {
    fontWeight: 700,
    marginBottom: theme.spacing(2),
    color: theme.palette.text.primary
  },
  emptyStateSubtitle: {
    marginBottom: theme.spacing(4),
    color: theme.palette.text.secondary,
    fontSize: '1.1rem',
    lineHeight: 1.6
  },
  featureList: {
    textAlign: 'left',
    marginBottom: theme.spacing(4),
    padding: theme.spacing(0, 4)
  },
  featureItem: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: theme.spacing(2),
    color: theme.palette.text.secondary
  },
  featureIcon: {
    marginRight: theme.spacing(2),
    color: theme.palette.success.main
  },
  ctaButton: {
    padding: theme.spacing(1.5, 4),
    fontSize: '1.1rem',
    fontWeight: 600,
    textTransform: 'none',
    boxShadow: '0 4px 14px rgba(0,118,255,0.39)',
    '&:hover': {
      boxShadow: '0 6px 20px rgba(0,118,255,0.5)',
      transform: 'translateY(-2px)'
    },
    transition: 'all 0.3s ease'
  },
  '@keyframes float': {
    '0%, 100%': {
      transform: 'translateY(0px)'
    },
    '50%': {
      transform: 'translateY(-20px)'
    }
  }
}));

const DashboardView = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const history = useHistory();
  const { userId } = useCurrentUserInfo();
  usePageTitle('Dashboard');
  const userJobs = useSelector(userJobListSelector);
  const userEnrichedJobs = useSelector(userEnrichedJobListSelector);
  const userJobsFetched = useSelector(userJobListFetchedSelector);
  const userJobsLoading = useSelector(userJobListLoadingSelector);
  const paymentRequiredJob = useSelector(paymentRequiredJobSelector);
  const paymentDialogOpen = useSelector(paymentDialogOpenSelector);
  const deleteConfirmationJob = useSelector(deleteConfirmationJobSelector);
  const deleteDialogOpen = useSelector(deleteDialogOpenSelector);
  const updatingJobId = useSelector(updatingJobIdSelector);
  const applicationsFetched = useSelector(applicationsFetchedSelector);
  const refreshUserJobs = useSelector(refreshUserJobsSelector);
  const loadingStatusCredits = useSelector(bundleLoadingSelector);
  const lastFetched = useSelector(bundleLastFetchedSelector);
  const bundleHistoryLoading = useSelector(bundleHistoryLoadingSelector);
  const bundleHistoryLastFetched = useSelector(
    bundleHistoryLastFetchedSelector
  );

  // Fetch bundle status on mount
  useEffect(() => {
    if (!loadingStatusCredits && !lastFetched) {
      console.log('💳 [DashboardView] Fetching bundle status for user.');
      dispatch(fetchBundleStatus());
    }
  }, [dispatch, loadingStatusCredits, lastFetched]);

  useEffect(() => {
    if (!bundleHistoryLoading && !bundleHistoryLastFetched) {
      console.log('💳 [DashboardView] Fetching bundle history for user.');
      dispatch(fetchBundleHistory());
    }
  }, [dispatch, bundleHistoryLoading, bundleHistoryLastFetched]);

  // Refresh dasboard view when user info changes if refreshUserJobs is true
  useEffect(() => {
    if (refreshUserJobs) {
      dispatch(clearUserJobs());
      dispatch(resetRefreshUserJobs());
    }
  }, [dispatch, refreshUserJobs]);

  // Fetch user's jobs from API using thunk
  useEffect(() => {
    // Only fetch if we have a userId and haven't fetched yet and not currently loading
    if (userId && !userJobsFetched && !userJobsLoading) {
      console.log(
        '🔄 [DashboardView] Dispatching fetchUserJobs for userId:',
        userId
      );
      dispatch(fetchUserJobs({ userId }));
    }
  }, [dispatch, userId, userJobsFetched, userJobsLoading]);

  // Fetch applications for company's jobs
  useEffect(() => {
    // Only fetch if we have jobs and haven't fetched applications yet
    if (userJobsFetched && !applicationsFetched) {
      console.log('🔄 [DashboardView] Fetching applications for company jobs');
      dispatch(fetchCompanyApplications());
    }
  }, [dispatch, userJobsFetched, applicationsFetched]);

  // Track dashboard view when jobs are loaded
  useEffect(() => {
    if (userJobsFetched && userJobs) {
      trackDashboardView(userJobs.length);
    }
  }, [userJobsFetched, userJobs]);

  // Memoize the edit handler
  const handleEditJob = useCallback(
    (job) => {
      trackJobEditClicked(job.id, job.title);
      dispatch(draftJobUpdated({ job }));
    },
    [dispatch]
  );

  // Memoize the toggle status handler
  const handleToggleStatus = useCallback(
    async (job) => {
      // Prevent multiple calls if already updating
      if (updatingJobId === job.id) {
        return;
      }

      // Validate ownership - ensure the job belongs to the current user
      if (job.userId !== userId) {
        console.error(
          '❌ [DashboardView] Ownership validation failed',
          '\n  Job userId:',
          job.userId,
          '\n  Current userId:',
          userId
        );
        return;
      }

      const newStatus =
        job.status === STATUS_ACTIVE ? STATUS_INACTIVE : STATUS_ACTIVE;
      const updatedJob = {
        ...job,
        status: newStatus,
        userId: userId // Explicitly set userId to ensure it's always present
      };

      console.log('🔄 [DashboardView] Toggling job status:', {
        jobId: job.id,
        oldStatus: job.status,
        newStatus: newStatus,
        userId: userId
      });

      dispatch(setUpdatingJobId({ jobId: job.id }));

      try {
        const result = dispatch(updateUserJob({ job: updatedJob }));

        // If result is null, it means payment is required (402 error)
        if (result === null) {
          console.log(
            '💳 [DashboardView] Payment required - dialog will be shown'
          );
          // Track payment required event
          trackJobActivationPaymentRequired(job.id, job.title);
        } else {
          // Track successful status change
          if (newStatus === STATUS_ACTIVE) {
            trackJobActivated(job.id, job.title);

            // Refresh bundle status after publishing (credit was consumed)
            console.log(
              '💳 [DashboardView] Job published, refreshing bundle status'
            );
            dispatch(fetchBundleStatus());
            dispatch(fetchBundleHistory());
          } else {
            trackJobDeactivated(job.id, job.title);
          }
        }

        dispatch(clearUpdatingJobId());
      } catch (error) {
        console.error('❌ [DashboardView] Failed to toggle job status:', error);
        dispatch(clearUpdatingJobId());
      }
    },
    [dispatch, updatingJobId, userId]
  );

  // Handle buy plan button click
  const handleBuyPlan = useCallback(() => {
    if (paymentRequiredJob) {
      console.log('💳 [DashboardView] Preparing to navigate to plan package');
      console.log(
        '📋 [DashboardView] Payment required job:',
        paymentRequiredJob
      );

      // Track buy plan click
      trackBuyPlanClicked(paymentRequiredJob.id, paymentRequiredJob.title);

      // Find the enriched job data (with company and file) from userEnrichedJobs
      const enrichedJobData = userEnrichedJobs.find(
        (jobResult) =>
          jobResult.job && jobResult.job.id === paymentRequiredJob.id
      );

      console.log('📦 [DashboardView] Enriched job data:', enrichedJobData);

      // Set job in draft for preview
      dispatch(draftJobUpdated({ job: paymentRequiredJob }));

      // Set company in draft if available
      if (enrichedJobData && enrichedJobData.company) {
        console.log(
          '🏢 [DashboardView] Setting draft company:',
          enrichedJobData.company
        );
        dispatch(draftCompanyUpdated({ company: enrichedJobData.company }));
      } else {
        console.warn('⚠️ [DashboardView] No company data found for job');
      }

      // Set file (logo) in draft if available
      if (enrichedJobData && enrichedJobData.file) {
        console.log(
          '📄 [DashboardView] Setting draft file:',
          enrichedJobData.file
        );
        dispatch(createFileForUserSucceeded({ file: enrichedJobData.file }));
      } else {
        console.warn('⚠️ [DashboardView] No file data found for job');
      }

      // Close dialog via Redux
      dispatch(closePaymentDialog());

      // Navigate to plan package page
      history.push(APP_PATH_PACKAGE_PLAN);
    }
  }, [paymentRequiredJob, userEnrichedJobs, dispatch, history]);

  // Handle dialog close
  const handleClosePaymentDialog = useCallback(() => {
    if (paymentRequiredJob) {
      trackPaymentDialogDismissed(paymentRequiredJob.id);
    }
    dispatch(closePaymentDialog());
  }, [dispatch, paymentRequiredJob]);

  // Memoize the delete handler
  const handleDeleteJob = useCallback(
    (job) => {
      // Prevent multiple calls if already updating
      if (updatingJobId === job.id) {
        return;
      }

      // Track dialog shown
      trackDeleteDialogShown(job.id, job.title, job.status === STATUS_ACTIVE);

      // Open the delete confirmation dialog via Redux
      dispatch(openDeleteConfirmationDialog({ job }));
    },
    [dispatch, updatingJobId]
  );

  // Handle confirming job deletion
  const handleConfirmDelete = useCallback(async () => {
    if (!deleteConfirmationJob) return;

    dispatch(setUpdatingJobId({ jobId: deleteConfirmationJob.id }));

    try {
      dispatch(deleteUserJob({ jobId: deleteConfirmationJob.id }));
      trackJobDeleted(deleteConfirmationJob.id, deleteConfirmationJob.title);
      dispatch(clearUpdatingJobId());
      dispatch(closeDeleteConfirmationDialog());
    } catch (error) {
      console.error('Failed to delete job:', error);
      dispatch(clearUpdatingJobId());
    }
  }, [dispatch, deleteConfirmationJob]);

  // Handle deactivating job instead of deleting
  const handleDeactivateInsteadOfDelete = useCallback(async () => {
    if (!deleteConfirmationJob) return;

    // Track user chose deactivation
    trackDeactivateInsteadOfDelete(
      deleteConfirmationJob.id,
      deleteConfirmationJob.title
    );

    // Only deactivate if job is currently active
    if (deleteConfirmationJob.status === STATUS_ACTIVE) {
      const deactivatedJob = {
        ...deleteConfirmationJob,
        status: STATUS_INACTIVE,
        userId: userId
      };

      dispatch(setUpdatingJobId({ jobId: deleteConfirmationJob.id }));

      try {
        dispatch(updateUserJob({ job: deactivatedJob }));
        trackJobDeactivated(
          deleteConfirmationJob.id,
          deleteConfirmationJob.title
        );
        dispatch(clearUpdatingJobId());
        dispatch(closeDeleteConfirmationDialog());
        dispatch(
          messageUpdated({
            text: '✅ Job deactivated successfully! Your data is safe and you can reactivate anytime.',
            severity: Severity.SUCCESS
          })
        );
      } catch (error) {
        console.error('Failed to deactivate job:', error);
        dispatch(clearUpdatingJobId());
      }
    } else {
      // If job is already inactive, just close the dialog
      dispatch(closeDeleteConfirmationDialog());
    }
  }, [dispatch, deleteConfirmationJob, userId]);

  // Handle keeping the job (closing dialog)
  const handleKeepJob = useCallback(() => {
    if (deleteConfirmationJob) {
      trackDeleteDialogKept(deleteConfirmationJob.id);
    }
    dispatch(closeDeleteConfirmationDialog());
  }, [dispatch, deleteConfirmationJob]);

  // Handle geo-lock toggle
  const handleToggleGeoLock = useCallback(
    async (job, plan) => {
      try {
        console.log('🔒 [DashboardView] Toggling geo-lock for job:', job.id);
        console.log('📦 [DashboardView] Current plan:', plan);

        const currentGeoLockStatus =
          plan.metadata && plan.metadata.geoLockEnabled === true;
        const newGeoLockStatus = !currentGeoLockStatus;

        console.log(
          `🔒 [DashboardView] Changing geo-lock from ${currentGeoLockStatus} to ${newGeoLockStatus}`
        );

        // Update plan metadata via PATCH
        const localAuth = getLocalStorageAuthentication();
        const url = `${BASE_END_POINT_COMPANY}/api/v1/plan/${plan.id}`;

        // Create updated metadata
        const updatedMetadata = {
          ...(plan.metadata || {}),
          geoLockEnabled: newGeoLockStatus
        };

        const response = await fetch(url, {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${localAuth.token}`
          },
          body: JSON.stringify({
            metadata: updatedMetadata
          })
        });

        if (!response.ok) {
          throw new Error(`Failed to update plan: ${response.status}`);
        }

        console.log('✅ [DashboardView] Geo-lock toggled successfully');

        dispatch(
          messageUpdated({
            text: `Geo-lock ${
              newGeoLockStatus ? 'enabled' : 'disabled'
            } successfully!`,
            severity: Severity.SUCCESS
          })
        );

        // Refresh jobs to get updated plan data
        dispatch(clearUserJobs());
        setTimeout(() => {
          dispatch(fetchUserJobs({ userId }));
        }, 500);
      } catch (error) {
        console.error('❌ [DashboardView] Failed to toggle geo-lock:', error);
        dispatch(
          messageUpdated({
            text: 'Failed to update geo-lock setting. Please try again.',
            severity: Severity.ERROR
          })
        );
      }
    },
    [dispatch, userId]
  );

  // Handle Post Job button click
  const handlePostFirstJob = () => {
    // Track the CTA click
    trackPostFirstJobClicked();

    // Clear only the job draft to start fresh with a new job
    // Keep company and file drafts so they can be used in the package preview
    clearDraftJobSession();

    // Navigate to job editor
    history.push(APP_PATH_EDITOR_JOB);
  };

  // Render loading state
  if (userJobsLoading && !userJobsFetched) {
    return (
      <div className={classes.loadingContainer}>
        <CircularProgress
          size={80}
          thickness={3}
          className={classes.loadingIcon}
        />
        <Typography variant="h4" gutterBottom>
          Loading Your Jobs...
        </Typography>
        <Typography color="textSecondary" variant="body1">
          We're gathering your job postings. This will just take a moment!{' '}
          <span role="img" aria-label="sparkles">
            ✨
          </span>
        </Typography>
      </div>
    );
  }

  // Render empty state
  if (userJobsFetched && (!userJobs || userJobs.length === 0)) {
    return (
      <div className={classes.emptyStateContainer}>
        <Card className={classes.emptyStateCard} elevation={3}>
          <CardContent>
            <RocketLaunchIcon className={classes.rocketIcon} />

            <Typography variant="h3" className={classes.emptyStateTitle}>
              Ready to Launch Your First Job?{' '}
              <span role="img" aria-label="rocket">
                🚀
              </span>
            </Typography>

            <Typography variant="body1" className={classes.emptyStateSubtitle}>
              You haven't posted any jobs yet, but you're just one click away
              from connecting with exceptional remote talent from around the
              world!
            </Typography>

            <Box className={classes.featureList}>
              <div className={classes.featureItem}>
                <WorkIcon className={classes.featureIcon} />
                <Typography variant="body1">
                  <strong>Reach Global Talent:</strong> Access thousands of
                  qualified remote professionals
                </Typography>
              </div>
              <div className={classes.featureItem}>
                <WorkIcon className={classes.featureIcon} />
                <Typography variant="body1">
                  <strong>Simple & Fast:</strong> Create and publish your job
                  posting in minutes
                </Typography>
              </div>
              <div className={classes.featureItem}>
                <WorkIcon className={classes.featureIcon} />
                <Typography variant="body1">
                  <strong>Quality Candidates:</strong> Get applications from
                  pre-vetted remote workers
                </Typography>
              </div>
            </Box>

            <Button
              variant="contained"
              color="primary"
              size="large"
              className={classes.ctaButton}
              onClick={handlePostFirstJob}
              startIcon={<RocketLaunchIcon />}>
              Post Your First Job
            </Button>

            <Box mt={3}>
              <Typography variant="body2" color="textSecondary">
                Join thousands of companies building world-class distributed
                teams
              </Typography>
            </Box>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Render jobs list
  return (
    <div className={classes.root}>
      <Grid container spacing={4}>
        <Grid item lg={12} md={12} xl={12} xs={12}>
          <JobListByUser
            jobs={userJobs}
            enrichedJobs={userEnrichedJobs}
            editJob={handleEditJob}
            toggleJobStatus={handleToggleStatus}
            toggleGeoLock={handleToggleGeoLock}
            deleteJob={handleDeleteJob}
            updatingJobId={updatingJobId}
          />
        </Grid>
      </Grid>

      {/* Payment Required Dialog */}
      <PaymentRequiredDialog
        open={paymentDialogOpen}
        onClose={handleClosePaymentDialog}
        onBuyPlan={handleBuyPlan}
        jobTitle={paymentRequiredJob?.title || ''}
      />

      {/* Delete Job Confirmation Dialog */}
      <DeleteJobConfirmationDialog
        open={deleteDialogOpen}
        onKeepJob={handleKeepJob}
        onDeactivateJob={handleDeactivateInsteadOfDelete}
        onDeleteJob={handleConfirmDelete}
        jobTitle={deleteConfirmationJob?.title || ''}
        isActive={deleteConfirmationJob?.status === STATUS_ACTIVE}
      />
    </div>
  );
};

export default DashboardView;
